import classnames from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { capitalizeWord } from '../../../utils';
import Tooltip from '../../generic/Tooltip';
import type { Severity } from '../../../../shared/organizationPackages';

import css from './VulnBadgeCounter.module.less';

type VulnBadgeCounterProps = {
  /**
   * The severity of the vulnerability. (e.g, 'critical', 'high', 'medium', 'low', 'unknown')
   */
  severity: Severity;
  /**
   * The count of the vulnerability with the given severity
   */
  count: number;
  /**
   * Should display an empty badge. Empty badge has a count of 0 and a gray background color.
   */
  isEmpty?: boolean;
};

const abbreviateSeverity = (severity: Severity) => {
  if (severity === 'unknown') {
    return '?';
  }
  return severity.slice(0, 1).toUpperCase();
};

/**
 * Display vuln severity and count in a badge.
 *
 * @param {'critical' | 'high' | 'medium' | 'low' | 'unknown'} severity - The severity of the vulnerability. (e.g, 'critical', 'high', 'medium', 'low', 'unknown')
 * @param {number} count - The count of the vulnerability with the given severity
 * @param {boolean} isEmpty - Should display an empty badge. Empty badge has a count of 0 and a gray background color.
 */
const VulnBadgeCounter: VoidFunctionComponent<VulnBadgeCounterProps> = ({ severity, count, isEmpty }) => {
  return (
    <Tooltip
      id={severity}
      showTooltip
      tooltip={
        <div className={css.tooltip}>
          <div>{capitalizeWord(severity)} severity vulnerabilities</div>
          <div>{count}</div>
        </div>
      }
    >
      <div className={css.vulnBadgeCounter}>
        <span className={classnames(css.severity, { [css[severity]]: !isEmpty, [css.empty]: isEmpty })}>
          {abbreviateSeverity(severity)}
        </span>
        <span className={css.count}>{isEmpty ? 0 : count}</span>
      </div>
    </Tooltip>
  );
};

export default VulnBadgeCounter;
